import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, BookOpen, Globe, Scale, MapPin, FileText, Calendar, MessageCircle, Newspaper } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { searchContent, type SearchResult } from "@/lib/search-index";
import type { ForumPost } from "@shared/schema";

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const typeConfig: Record<SearchResult["type"], { label: string; icon: typeof Search }> = {
  faq: { label: "Knowledge Base", icon: BookOpen },
  country: { label: "Country Guides", icon: Globe },
  legislation: { label: "Legislation", icon: Scale },
  provision: { label: "Legislation", icon: Scale },
  "regional-center": { label: "Regional Centers", icon: MapPin },
  article: { label: "News & Analysis", icon: Newspaper },
  page: { label: "Pages", icon: FileText },
};

const sectionOrder = ["News & Analysis", "Knowledge Base", "Country Guides", "Visa Bulletin", "Legislation", "Regional Centers", "Forum", "Pages"];

const sectionIcons: Record<string, typeof Search> = {
  "Visa Bulletin": Calendar,
  "Forum": MessageCircle,
  "News & Analysis": Newspaper,
};

function groupResults(results: SearchResult[]) {
  const groups: Record<string, SearchResult[]> = {};
  for (const result of results) {
    const label = result.category && sectionOrder.includes(result.category) ? result.category : typeConfig[result.type]?.label || "Pages";
    if (!groups[label]) groups[label] = [];
    groups[label].push(result);
  }
  return sectionOrder
    .filter((s) => groups[s])
    .map((label) => ({ label, results: groups[label] }));
}

function truncateSnippet(text: string, max = 120) {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "...";
}

function searchForumPosts(posts: ForumPost[], query: string): SearchResult[] {
  if (!query.trim()) return [];
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const results: { result: SearchResult; score: number }[] = [];

  for (const post of posts) {
    const titleLower = post.title.toLowerCase();
    const contentLower = post.content.toLowerCase();
    const searchable = `${titleLower} ${contentLower} ${post.category.toLowerCase()} ${post.authorName.toLowerCase()}`;
    let allMatch = true;
    let score = 0;

    for (const word of words) {
      const inTitle = titleLower.includes(word);
      const inContent = searchable.includes(word);
      if (!inTitle && !inContent) {
        allMatch = false;
        break;
      }
      if (inTitle) score += 3;
      if (inContent) score += 1;
    }

    if (allMatch) {
      results.push({
        result: {
          id: `forum-${post.id}`,
          type: "page",
          title: post.title,
          snippet: post.content,
          url: "/forum",
          category: "Forum",
        },
        score,
      });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, 5).map((r) => r.result);
}

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const [, setLocation] = useLocation();

  const { data: forumPosts, isLoading: forumLoading } = useQuery<ForumPost[]>({
    queryKey: ["/api/forum/posts"],
    enabled: open,
  });

  const staticResults = searchContent(query);
  const forumResults = useMemo(
    () => searchForumPosts(forumPosts || [], query),
    [forumPosts, query]
  );
  const allResults = useMemo(() => [...staticResults, ...forumResults], [staticResults, forumResults]);
  const grouped = groupResults(allResults);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  function handleSelect(result: SearchResult) {
    setLocation(result.url);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 top-[20%] translate-y-0"
        aria-describedby={undefined}
        data-testid="search-dialog"
      >
        <span className="sr-only"><DialogTitle>Search</DialogTitle></span>
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search EB-5 resources..."
            className="flex-1 bg-transparent text-lg outline-none placeholder:text-muted-foreground"
            data-testid="input-search"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs text-muted-foreground">
            <span className="text-xs">ESC</span>
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!query.trim() && (
            <div className="px-4 py-8 text-center text-muted-foreground" data-testid="text-search-hint">
              Search across all EB-5 resources...
            </div>
          )}
          {query.trim() && allResults.length === 0 && !forumLoading && (
            <div className="px-4 py-8 text-center text-muted-foreground" data-testid="text-no-results">
              No results found for "{query}"
            </div>
          )}
          {query.trim() && forumLoading && allResults.length === 0 && (
            <div className="px-4 py-8 text-center text-muted-foreground">
              Searching...
            </div>
          )}
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.label}
              </div>
              {group.results.map((result) => {
                const Icon = sectionIcons[group.label] || typeConfig[result.type]?.icon || FileText;
                return (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover-elevate rounded-md cursor-pointer"
                    data-testid={`button-search-result-${result.id}`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {result.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {truncateSnippet(result.snippet)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
